// The upload wizard's location step, reduced to the parts that are decidable
// without a map, a network call, or a mounted component.
//
// Pure on purpose, like wizard-route.js: what arrives here is whatever Google
// handed the component -- a LatLng whose accessors are functions, a geocoder
// response array that may be null, a Places result assembled from a `fields`
// list somebody edited. All of it is hostile input in the same sense the
// `labels` HTML attribute is, and none of it needs a Google map instance to
// reason about. Vitest cannot mount .vue files in this repo (vitest.config.mjs),
// so a rule that lives in the component is a rule with no test.

/**
 * Normalises whatever carries a latitude and a longitude into a plain
 * `{lat, lng}` of numbers, or `null` when it cannot prove it has both.
 *
 * Two shapes reach the same handler in this step, which is the whole reason
 * this exists:
 *
 * - `google.maps.LatLng` -- what `place.geometry.location` is on a Places
 *   selection, what `event.latLng` is on a map `click`, and what
 *   `marker.getPosition()` returns after a `dragend`. Its `lat` and `lng` are
 *   METHODS. Reading them as properties yields two functions, and
 *   `art.lat = function` is posted to /api/arts.json without throwing anywhere
 *   along the way.
 * - A plain literal -- `item.position`, `item.center`, and everything the EXIF
 *   and `getCurrentPosition` paths already write.
 *
 * Returns `null` rather than falling back to `{lat: 0, lng: 0}`. That default
 * is not neutral: 0,0 is Null Island, a buoy in the Gulf of Guinea, and it is
 * a coordinate the map will happily draw and the API will happily store. A
 * rejected event leaves the pin where the visitor last put it, which is the
 * only failure of the two they can see and correct.
 *
 * @param {object} value A google.maps.LatLng, or `{lat, lng}`.
 * @returns {{lat: number, lng: number}|null}
 */
export function toLatLngLiteral(value) {
  // Arrays and functions are objects too; neither carries lat/lng in a form
  // worth guessing at, and `typeof null === 'object'` is the classic hole.
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const lat = typeof value.lat === 'function' ? value.lat() : value.lat
  const lng = typeof value.lng === 'function' ? value.lng() : value.lng

  // No coercion on the way in, deliberately. `Number('')`, `Number(null)` and
  // `Number([])` are all 0, so a Number() here is exactly how an empty or
  // missing field becomes Null Island. NaN and Infinity fail the same check
  // for a second reason: `JSON.stringify` renders both as `null`, so they
  // would reach the API as a piece with no location and no error to explain it.
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  return { lat, lng }
}

/**
 * The one address string to show for a reverse-geocode response.
 *
 * `google.maps.Geocoder.geocode({location})` answers with an ARRAY ordered
 * most specific first -- street address, then block, then neighbourhood, then
 * city, then country -- and the wizard's Address field holds one string. So:
 * the first entry that actually carries an address.
 *
 * The first entry that CARRIES one, not `results[0]`. Reverse geocoding a
 * point in a park, an alley or a plaza routinely leads with a plus-code entry
 * whose `formatted_address` is absent or empty, and blanking the field while a
 * usable street address sits one index along is the same silent disagreement
 * between pin and text that this step exists to prevent.
 *
 * Always returns a string, including for a failed lookup. The caller assigns it
 * to `art.address`, which is posted verbatim, and it needs to be able to tell
 * "no address" from "an address" so it can say so out loud rather than leave
 * text describing somewhere the pin no longer is.
 *
 * @param {Array<object>} results First argument of the geocode callback.
 * @returns {string} A formatted address, or '' when there is none.
 */
export function pickGeocodedAddress(results) {
  // The callback signature is (results, status): on any status other than OK
  // -- ZERO_RESULTS over open water, OVER_QUERY_LIMIT, REQUEST_DENIED -- the
  // first argument is null.
  if (!Array.isArray(results)) return ''

  for (const result of results) {
    if (!result || typeof result.formatted_address !== 'string') continue
    const address = result.formatted_address.trim()
    if (address) return address
  }

  return ''
}

export default toLatLngLiteral
